import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Plus, ShieldCheck, ShieldOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useConfirm } from '@/components/ui/confirm-dialog'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { ProductionDocumentSource, ProductionDocumentVersion } from '@/types'

type Props = {
  entityId: string
  sources: ProductionDocumentSource[]
}

type VersionState = 'ready' | 'draft' | 'retired' | 'digest_changed' | 'not_ready'

function versionState(version: ProductionDocumentVersion): VersionState {
  if (version.ready) return 'ready'
  if (version.status === 'retired') return 'retired'
  if (version.status === 'draft') return 'draft'
  if (version.status === 'approved' && !version.digest_matches) return 'digest_changed'
  return 'not_ready'
}

function StateBadge({ state }: { state: VersionState }) {
  switch (state) {
    case 'ready':
      return <Badge>Ready</Badge>
    case 'draft':
      return <Badge variant="outline">Draft</Badge>
    case 'retired':
      return <Badge variant="outline">Retired</Badge>
    case 'digest_changed':
      return <Badge variant="destructive">Digest changed</Badge>
    default:
      return <Badge variant="outline">Not ready</Badge>
  }
}

function formatTimestamp(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function VersionDetails({ label, version, action }: { label: string; version: ProductionDocumentVersion; action?: React.ReactNode }) {
  const state = versionState(version)
  return (
    <div className="mt-2 border-t pt-2 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
          {version.version_number && <span className="text-xs">v{version.version_number}</span>}
          <StateBadge state={state} />
        </div>
        <div className="mt-1 text-xs text-muted-foreground space-x-3">
          {version.digest_short && (
            <span className={version.digest_matches ? '' : 'text-destructive'}>
              {version.digest_short}{version.digest_matches ? '' : ' (stale)'}
            </span>
          )}
          {version.legal_review_reference && <span>{version.legal_review_reference}</span>}
          {version.source_revision && <span>rev {version.source_revision}</span>}
        </div>
        <div className="mt-1 text-xs text-muted-foreground space-x-3">
          {version.approved_by && (
            <span>Approved by {version.approved_by} · {formatTimestamp(version.approved_at)}</span>
          )}
          {version.retired_by && (
            <span>Retired by {version.retired_by} · {formatTimestamp(version.retired_at)}</span>
          )}
        </div>
      </div>
      {action && <div className="flex items-center gap-1.5 shrink-0">{action}</div>}
    </div>
  )
}

export function ProductionDocumentSources({ entityId, sources }: Props) {
  const confirm = useConfirm()
  const [versionTarget, setVersionTarget] = useState<ProductionDocumentSource | null>(null)
  const [legalReviewReference, setLegalReviewReference] = useState('')
  const [sourceRevision, setSourceRevision] = useState('')
  const [creating, setCreating] = useState(false)

  function openVersionDialog(source: ProductionDocumentSource) {
    setLegalReviewReference('')
    setSourceRevision('')
    setVersionTarget(source)
  }

  function initialize(source: ProductionDocumentSource) {
    router.post(
      `/backoffice/entities/${entityId}/document_templates`,
      { document_template: { code: source.code } },
      { preserveScroll: true },
    )
  }

  async function approve(source: ProductionDocumentSource) {
    if (!source.latest) return
    if (!await confirm({ description: `Approve v${source.latest.version_number} of "${source.name}"?`, confirmLabel: 'Approve' })) return
    router.post(
      `/backoffice/entities/${entityId}/document_templates/${source.template_id}/versions/${source.latest.version_id}/approval`,
      {},
      { preserveScroll: true },
    )
  }

  async function retire(source: ProductionDocumentSource) {
    if (!source.active) return
    if (!await confirm({ description: `Retire v${source.active.version_number} of "${source.name}"? It will no longer render in production.`, confirmLabel: 'Retire', destructive: true })) return
    router.post(
      `/backoffice/entities/${entityId}/document_templates/${source.template_id}/versions/${source.active.version_id}/retirement`,
      {},
      { preserveScroll: true },
    )
  }

  function submitVersion(e: React.FormEvent) {
    e.preventDefault()
    if (!versionTarget) return
    setCreating(true)
    router.post(
      `/backoffice/entities/${entityId}/document_templates/${versionTarget.template_id}/versions`,
      { version: { legal_review_reference: legalReviewReference, source_revision: sourceRevision } },
      {
        preserveScroll: true,
        onSuccess: () => setVersionTarget(null),
        onFinish: () => setCreating(false),
      },
    )
  }

  return (
    <section className="border rounded-lg">
      <div className="px-4 py-3 border-b">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Production document sources</h2>
      </div>

      <div className="divide-y">
        {sources.map((source) => {
          const active = source.active
          const latest = source.latest
          const latestIsActive = !!active && !!latest && active.version_id === latest.version_id

          return (
            <div key={source.code} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{source.name}</span>
                    <code className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{source.code}</code>
                    {!source.template_exists && <Badge variant="destructive">Missing</Badge>}
                    {source.template_exists && !latest && <Badge variant="outline">No version</Badge>}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {!source.template_exists && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => initialize(source)}>
                      Initialize
                    </Button>
                  )}
                  {source.template_exists && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => openVersionDialog(source)}>
                      <Plus className="h-3 w-3" /> New version
                    </Button>
                  )}
                </div>
              </div>

              {active && (
                <VersionDetails
                  label="Active"
                  version={active}
                  action={(
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-destructive hover:text-destructive" onClick={() => retire(source)}>
                      <ShieldOff className="h-3 w-3" /> Retire
                    </Button>
                  )}
                />
              )}
              {latest && !latestIsActive && (
                <VersionDetails
                  label="Latest"
                  version={latest}
                  action={latest.status === 'draft' && latest.digest_matches && (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => approve(source)}>
                      <ShieldCheck className="h-3 w-3" /> Approve
                    </Button>
                  )}
                />
              )}
            </div>
          )
        })}
      </div>

      <Dialog open={!!versionTarget} onOpenChange={(open) => { if (!open) setVersionTarget(null) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New internal version</DialogTitle>
            <DialogDescription>
              {versionTarget && <>Create the next internal-template version for {versionTarget.name}.</>}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitVersion} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="legal_review_reference">Legal review reference <span className="text-destructive">*</span></Label>
              <Input
                id="legal_review_reference"
                value={legalReviewReference}
                onChange={(e) => setLegalReviewReference(e.target.value)}
                placeholder="e.g. legal/2026-001"
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="source_revision">Source revision</Label>
              <Input
                id="source_revision"
                value={sourceRevision}
                onChange={(e) => setSourceRevision(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setVersionTarget(null)}>Cancel</Button>
              <Button type="submit" disabled={creating || !legalReviewReference}>
                {creating ? 'Creating...' : 'Create version'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
